import React from 'react'
import classnames from 'classnames'
import { Spacer, WorkArea } from '@revelrygroup/components'

import {
  ObituaryMemory as IObituaryMemory,
  ObituarySnippet,
  ReactionByID,
  VisitorUser,
} from '../../../types'
import FlowerRow from '../../FlowerRow'
import Obituary from '../../models/Obituary'
import ObituaryMemory from '../../ObituaryMemory'
import ObituaryMemoryForm, {
  FormValues as MemoryFormValues,
} from '../../../forms/ObituaryMemoryForm'
import styles from './styles.module.scss'
import useFlowerProductsContext from '../../../hooks/useFlowerProductsContext'

interface Props {
  canLoadMore: boolean
  formOpen: boolean
  initialValues: MemoryFormValues
  memories: { [memoryId: string]: IObituaryMemory }
  obituary: ObituarySnippet | null
  onLike: (memoryID: number, newUser?: VisitorUser) => Promise<void>
  onLoadMore: () => Promise<void>
  onSetFormOpen: () => void
  onSubmit: (values: MemoryFormValues) => Promise<void>
  reactionsByMemoryID: ReactionByID
  userMemories: { [memoryId: string]: IObituaryMemory }
  visitorUser?: VisitorUser
}

const MemoriesPage: React.FC<Props> = ({
  canLoadMore,
  formOpen,
  initialValues,
  memories,
  obituary,
  onLike,
  onLoadMore,
  onSetFormOpen,
  onSubmit,
  reactionsByMemoryID,
  userMemories,
  visitorUser,
}) => {
  const memoriesWrapper = React.useRef<HTMLDivElement>()
  const { products } = useFlowerProductsContext()
  const onFormSubmit = async (values: MemoryFormValues): Promise<void> => {
    try {
      await onSubmit(values)
      if (memoriesWrapper.current) {
        memoriesWrapper.current.scrollIntoView(true)
      }
    } catch {
      false
    }
  }
  const noMemories = Object.keys(memories).length === 0 && Object.keys(userMemories).length === 0

  return (
    <>
      <WorkArea avoidHeader className={classnames(styles.formWorkarea, 'print-hide')}>
        <div
          className={classnames(styles.memoryFormInner, {
            [styles.memoryFormInnerShow]: formOpen,
          })}
        >
          <h5 className={classnames('h5', styles.formTitle)}>
            Share A Memory of {Obituary.deceasedFullName(obituary)}
          </h5>

          <ObituaryMemoryForm
            canAddCandle
            deceasedFirstName={obituary.deceased.first_name}
            initialValues={initialValues}
            onSubmit={onFormSubmit}
          />
        </div>

        <button
          className={classnames('h6', styles.showFormButton, {
            [styles.showFormButtonHide]: formOpen,
          })}
          onClick={onSetFormOpen}
          type="button"
        >
          Share a memory
        </button>
      </WorkArea>

      <WorkArea>
        <Spacer verticalBottom="sm">
          <h1 className="h3 centered-text" ref={memoriesWrapper}>
            {Obituary.deceasedFullName(obituary)} Memories &amp; Tributes
          </h1>
        </Spacer>

        {noMemories ? (
          <p className="body">
            No memories or condolences have been added at this time. Be the first to leave a tribute
            using the form fields above.
          </p>
        ) : (
          <div className={styles.memoriesWrapper}>
            {Object.values(userMemories).map((obituaryMemory) => (
              <ObituaryMemory
                hasReaction={Boolean(reactionsByMemoryID[obituaryMemory.id])}
                key={obituaryMemory.id}
                obituary={obituary}
                obituaryMemory={obituaryMemory}
                onLike={onLike}
                visitorUser={visitorUser}
              />
            ))}

            {Object.values(memories).map((obituaryMemory) => (
              <ObituaryMemory
                hasReaction={Boolean(reactionsByMemoryID[obituaryMemory.id])}
                key={obituaryMemory.id}
                obituary={obituary}
                obituaryMemory={obituaryMemory}
                onLike={onLike}
                visitorUser={visitorUser}
              />
            ))}
          </div>
        )}

        {canLoadMore && (
          <div>
            <button className={styles.loadMoreBtn} onClick={() => onLoadMore()} type="button">
              Load more
            </button>
          </div>
        )}
      </WorkArea>

      {obituary.enable_flowerstore && products.length && (
        <FlowerRow obituary={obituary} products={products.slice(0, 11)} />
      )}
    </>
  )
}

export default MemoriesPage
